// ============================================================================
// ClinicalEvidenceChips — Phase 2C-4 (Gate evidence chips, READ-ONLY)
//
// The concept library is the underground river; Pack evidence maps are the
// channels; clinical evidence candidates are the signal; these chips are where
// the signal surfaces in the Gate. Chips are POINTERS only.
//
// HARD RULES (this layer is correct only if all hold):
//   - A chip never writes clinicalAnswers, never selects an option, never checks
//     a red flag, never marks an attachment satisfied, never changes the outcome.
//   - GP confirmation / manual answer remains the ONLY truth.
//   - Chips NEVER block Continue/Submit. Match/list is non-blocking + best-effort.
//   - Separate from the patient/admin OCR suggestions (which prefill + block).
//
// Distinct from PacketSuggestions: those are accept/dismiss prefill that blocks
// Continue. These are read-only assistive pointers with Dismiss only.
//
// PHI: evidenceSnippet / extractedValue are PHI and render in the Gate UI for the
// gp creating the referral. NEVER logged. No raw OCR text, filenames, or storage
// paths. All candidate access is via the gp-only Cloud Functions.
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sparkles, X, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import {
  matchPacketEvidenceToPack,
  listPacketEvidenceCandidates,
  reviewPacketEvidenceCandidate,
  CLINICAL_EVIDENCE_REVIEW_ACTION,
} from '../../core/store.packets.js';

const Ctx = createContext(null);

const DOC_TYPE_LABELS = {
  REFERRAL_LETTER: 'Referral letter', CLINICAL_NOTE: 'Clinical note', LAB_RESULT: 'Lab result',
  IMAGING_REPORT: 'Imaging report', INSURANCE_CARD: 'Insurance card', MEDICATION_LIST: 'Medication list',
  DISCHARGE_SUMMARY: 'Discharge summary', CONSULT_NOTE: 'Consult note', OTHER: 'Document', UNKNOWN: 'Document',
};
const docLabel = (t) => DOC_TYPE_LABELS[t] || 'Document';
const optionKey = (fieldId, optionValue) => `${fieldId} ${optionValue}`;

// ── Provider ─────────────────────────────────────────────────────────────────
// Runs match→list ONCE per (packetId + packDocId) per Gate session, holds
// candidate state, groups by target, and exposes read-only accessors + dismiss.

export const ClinicalEvidenceProvider = ({ packetId, specialtyId, packId, versionId, packDocId, children }) => {
  const enabled = Boolean(packetId && specialtyId && packId && versionId && packDocId);
  const [status, setStatus] = useState('idle');         // idle | searching | ready | error
  const [candidates, setCandidates] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set()); // optimistic dismiss
  const [dismissError, setDismissError] = useState(null);
  const ranKeyRef = useRef(null);

  const run = useCallback(async () => {
    if (!enabled) return;
    setStatus('searching');
    setDismissError(null);
    try {
      // Trigger the match (idempotent server-side). Best-effort: if it errors we
      // still try to list any candidates a prior run wrote. NEVER logs PHI.
      try {
        await matchPacketEvidenceToPack({ packetId, specialtyId, packId, versionId });
      } catch (e) {
        console.warn('[ClinicalEvidence] match non-fatal:', e?.message);
      }
      const res = await listPacketEvidenceCandidates({ packetId, packDocId });
      setCandidates(Array.isArray(res?.candidates) ? res.candidates : []);
      setDismissedIds(new Set());
      setStatus('ready');
    } catch (e) {
      console.warn('[ClinicalEvidence] list failed:', e?.message);
      setStatus('error');
    }
  }, [enabled, packetId, specialtyId, packId, versionId, packDocId]);

  // Run once per packet+pack key. Re-runs when the selected Pack changes.
  useEffect(() => {
    if (!enabled) { setStatus('idle'); setCandidates([]); ranKeyRef.current = null; return; }
    const key = `${packetId}:${packDocId}`;
    if (ranKeyRef.current === key) return;
    ranKeyRef.current = key;
    run();
  }, [enabled, packetId, packDocId, run]);

  const retryEvidenceSearch = useCallback(() => { run(); }, [run]);

  // Render filter: active (list already enforces) + not dismissed + POSITIVE only.
  const visible = useMemo(
    () => candidates.filter((c) => c && c.status !== 'DISMISSED' && c.polarity === 'POSITIVE' && !dismissedIds.has(c.candidateId)),
    [candidates, dismissedIds],
  );

  const groups = useMemo(() => {
    const field = {}, option = {}, redFlag = {}, attachment = {};
    for (const c of visible) {
      if (c.targetType === 'FIELD' && c.fieldId) (field[c.fieldId] = field[c.fieldId] || []).push(c);
      else if (c.targetType === 'OPTION' && c.fieldId && c.optionValue != null) {
        const k = optionKey(c.fieldId, c.optionValue);
        (option[k] = option[k] || []).push(c);
      } else if (c.targetType === 'RED_FLAG' && c.redFlagId) (redFlag[c.redFlagId] = redFlag[c.redFlagId] || []).push(c);
      else if (c.targetType === 'ATTACHMENT_REQUIREMENT' && c.attachmentRequirementId) (attachment[c.attachmentRequirementId] = attachment[c.attachmentRequirementId] || []).push(c);
    }
    return { field, option, redFlag, attachment };
  }, [visible]);

  const getEvidenceForField = useCallback((fieldId) => groups.field[fieldId] || [], [groups]);
  const getEvidenceForOption = useCallback((fieldId, optionValue) => groups.option[optionKey(fieldId, optionValue)] || [], [groups]);
  const getEvidenceForRedFlag = useCallback((redFlagId) => groups.redFlag[redFlagId] || [], [groups]);
  const getEvidenceForAttachment = useCallback((attachmentRequirementId) => groups.attachment[attachmentRequirementId] || [], [groups]);

  // Optimistic dismiss; restore + surface a small error on failure.
  const dismissCandidate = useCallback(async (candidateId) => {
    if (!candidateId) return;
    setDismissedIds((prev) => { const n = new Set(prev); n.add(candidateId); return n; });
    setDismissError(null);
    try {
      await reviewPacketEvidenceCandidate({ packetId, candidateId, action: CLINICAL_EVIDENCE_REVIEW_ACTION.DISMISS });
    } catch (e) {
      setDismissedIds((prev) => { const n = new Set(prev); n.delete(candidateId); return n; });
      setDismissError('Could not dismiss evidence. Please try again.');
    }
  }, [packetId]);

  const value = {
    enabled, status, dismissError, docLabel,
    getEvidenceForField, getEvidenceForOption, getEvidenceForRedFlag, getEvidenceForAttachment,
    dismissCandidate, retryEvidenceSearch,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

const useEvidence = () => useContext(Ctx);

// ── Chip primitive (subordinate, read-only; no Use / no toggle) ──────────────

const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };

function EvidenceChip({ lead, valueText, sub, onDismiss, tone = 'indigo' }) {
  const palette = tone === 'red'
    ? 'border-red-700/40 bg-red-950/20'
    : 'border-indigo-700/40 bg-indigo-950/20';
  return (
    // stopPropagation guards against a chip living inside/near a clickable
    // control (e.g. a red flag button) — a chip click never toggles anything.
    <div onClick={stop} className={`mt-1.5 rounded-md border ${palette} px-2.5 py-1.5 text-xs`}>
      <div className="flex items-start gap-1.5">
        <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-slate-400">{lead}</div>
          <div className="text-slate-100 break-words">“{valueText}”</div>
          {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <button
          type="button"
          onClick={(e) => { stop(e); onDismiss(); }}
          title="Dismiss"
          className="ml-1 shrink-0 text-slate-500 hover:text-slate-200"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Public chip components ────────────────────────────────────────────────────

// Field + its option evidence (option chips grouped below the field — non-invasive).
export function FieldEvidenceChips({ field }) {
  const ctx = useEvidence();
  if (!ctx || !ctx.enabled || !field) return null;
  const fieldChips = ctx.getEvidenceForField(field.id);
  const options = Array.isArray(field.options) ? field.options : [];
  const optionChips = [];
  for (const opt of options) {
    const val = (opt && typeof opt === 'object') ? opt.value : opt;
    if (val == null) continue;
    const label = (opt && typeof opt === 'object' && opt.label) ? opt.label : val;
    for (const c of ctx.getEvidenceForOption(field.id, val)) optionChips.push({ c, label });
  }
  if (fieldChips.length === 0 && optionChips.length === 0) return null;
  return (
    <div className="mt-1">
      {fieldChips.map((c) => (
        <EvidenceChip
          key={c.candidateId}
          lead="Possible packet evidence"
          valueText={(c.evidenceSnippet || '') + (c.unit ? ` ${c.unit}` : '')}
          sub={`Source: ${ctx.docLabel(c.documentType)}`}
          onDismiss={() => ctx.dismissCandidate(c.candidateId)}
        />
      ))}
      {optionChips.map(({ c, label }) => (
        <EvidenceChip
          key={c.candidateId}
          lead="Possible match"
          valueText={c.evidenceSnippet || ''}
          sub={`Matches option: ${label}`}
          onDismiss={() => ctx.dismissCandidate(c.candidateId)}
        />
      ))}
    </div>
  );
}

export function RedFlagEvidenceChips({ redFlagId }) {
  const ctx = useEvidence();
  if (!ctx || !ctx.enabled) return null;
  const chips = ctx.getEvidenceForRedFlag(redFlagId);
  if (chips.length === 0) return null;
  return (
    <div className="mt-1.5">
      {chips.map((c) => (
        <EvidenceChip
          key={c.candidateId}
          tone="red"
          lead="Possible red flag evidence"
          valueText={c.evidenceSnippet || ''}
          sub="Review before selecting — this does not select the red flag."
          onDismiss={() => ctx.dismissCandidate(c.candidateId)}
        />
      ))}
    </div>
  );
}

export function AttachmentEvidenceChips({ attachmentRequirementId }) {
  const ctx = useEvidence();
  if (!ctx || !ctx.enabled) return null;
  const chips = ctx.getEvidenceForAttachment(attachmentRequirementId);
  if (chips.length === 0) return null;
  return (
    <div className="mt-1.5">
      {chips.map((c) => (
        <EvidenceChip
          key={c.candidateId}
          lead="Possible document evidence"
          valueText={c.evidenceSnippet || ''}
          sub={`Source: ${ctx.docLabel(c.documentType)}`}
          onDismiss={() => ctx.dismissCandidate(c.candidateId)}
        />
      ))}
    </div>
  );
}

// Subtle, non-blocking status line. Searching → spinner; error → retry; ready/no
// evidence → silent (chips speak for themselves). Never blocks Continue.
export function ClinicalEvidenceNote() {
  const ctx = useEvidence();
  if (!ctx || !ctx.enabled) return null;
  if (ctx.status === 'searching') {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching packet evidence…
      </div>
    );
  }
  if (ctx.status === 'error') {
    return (
      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-amber-700/40 bg-amber-900/20 text-xs text-amber-200">
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">Could not search packet evidence. You can continue manually.</span>
        <button type="button" onClick={ctx.retryEvidenceSearch}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-600/50 hover:bg-amber-800/30">
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }
  if (ctx.dismissError) {
    return <div className="text-[11px] text-amber-300">{ctx.dismissError}</div>;
  }
  return null;
}

export default ClinicalEvidenceProvider;
